import React from 'react';
import { Link } from 'react-router';

/**
 * The two suggestion empty states, shared by the discovery surface and the
 * /trade-offers zero-state strip.
 *
 * Two densities, one definition (per convention: extend the component rather
 * than fork a local variant):
 *   - 'page'  fills the surface's content area, the designed treatment with a
 *             badge, a heading and both actions.
 *   - 'strip' is the compact paragraph the offers zero state has always shown,
 *             where a full-height empty state would dwarf its own section.
 *
 * Copy is the spec's Reference Copy verbatim, split at the first sentence so
 * the opening line can carry the heading.
 */

type Variant = 'page' | 'strip';

interface EmptyProps {
  variant?: Variant;
}

const ACTIONS = (
  <div className="sugg-empty-actions">
    <Link className="sugg-empty-action" to="/my-cards/want-list">
      Add cards to your want list
    </Link>
    <Link className="sugg-empty-action" to="/my-cards/have-list">
      Mark more of your collection for trade
    </Link>
  </div>
);

interface ShellProps {
  tone: 'done' | 'none';
  glyph: string;
  heading: string;
  body: string;
}

const EmptyShell: React.FC<ShellProps> = ({ tone, glyph, heading, body }) => (
  <div className={`sugg-empty sugg-empty--${tone}`}>
    <span className="sugg-empty-badge" aria-hidden="true">
      {glyph}
    </span>
    <h2 className="sugg-empty-heading">{heading}</h2>
    <p className="sugg-empty-body">{body}</p>
    {ACTIONS}
  </div>
);

/** Everything has been acted on. Still offers the two actions: the way to get
 *  more suggestions is to widen the lists they are computed from. */
export const SuggestionsEmptyAllHandled: React.FC<EmptyProps> = ({ variant = 'strip' }) => {
  if (variant === 'strip') {
    return (
      <div className="offers-zero-sugg-empty">
        <p>
          You&apos;re all caught up. You&apos;ve acted on every suggestion for now. Check back
          soon. New matches appear as collections change.
        </p>
      </div>
    );
  }
  return (
    <EmptyShell
      tone="done"
      glyph="&#10003;"
      heading="You're all caught up"
      body="You've acted on every suggestion for now. Check back soon. New matches appear as collections change."
    />
  );
};

/** Nothing has ever matched. The two actions ARE the "two things" the copy
 *  refers to, so they carry the sentence rather than merely following it. */
export const SuggestionsEmptyNoMatches: React.FC<EmptyProps> = ({ variant = 'strip' }) => {
  if (variant === 'strip') {
    return (
      <div className="offers-zero-sugg-empty">
        <p>
          No trades to suggest yet. Suggestions appear when someone holds a card you want and
          wants a card you hold. Two things make that likely.
        </p>
        {ACTIONS}
      </div>
    );
  }
  return (
    <EmptyShell
      tone="none"
      glyph="&#43;"
      heading="No trades to suggest yet"
      body="Suggestions appear when someone holds a card you want and wants a card you hold. Two things make that likely."
    />
  );
};
